import { defineMessages } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import type { ConditionalKeys } from 'type-fest';
import { LevelValueEnum } from '../types';
import type { ClassificationMarkStringArrayKey, ClassificationMark } from '../types';
import { isSubset } from './utils';

const messages = defineMessages({
  classificationLevel: {
    id: 'IsAtLeastAsRestrictive.classificationLevel',
    defaultMessage: 'Classification level too low',
  },
  sci: {
    id: 'IsAtLeastAsRestrictive.sci',
    defaultMessage: 'SCI mismatch',
  },
  sap: {
    id: 'IsAtLeastAsRestrictive.sap',
    defaultMessage: 'SAP mismatch',
  },
  fgi: {
    id: 'IsAtLeastAsRestrictive.fgi',
    defaultMessage: 'FGI mismatch',
  },
  fouo: {
    id: 'IsAtLeastAsRestrictive.fouo',
    defaultMessage: 'FOUO missing',
  },
  dcni: {
    id: 'IsAtLeastAsRestrictive.dcni',
    defaultMessage: 'DCNI missing',
  },
  cui: {
    id: 'IsAtLeastAsRestrictive.cui',
    defaultMessage: 'CUI mismatch',
  },
  sbu: {
    id: 'IsAtLeastAsRestrictive.sbu',
    defaultMessage: 'SBU mismatch',
  },
  imc: {
    id: 'IsAtLeastAsRestrictive.imc',
    defaultMessage: 'IMC missing',
  },
  pr: {
    id: 'IsAtLeastAsRestrictive.pr',
    defaultMessage: 'PR missing',
  },
  fisa: {
    id: 'IsAtLeastAsRestrictive.fisa',
    defaultMessage: 'FISA missing',
  },
  nf: {
    id: 'IsAtLeastAsRestrictive.nf',
    defaultMessage: 'NF missing',
  },
  relTo: {
    id: 'IsAtLeastAsRestrictive.relTo',
    defaultMessage: 'REL TO has extra entities',
  },
  displayOnly: {
    id: 'IsAtLeastAsRestrictive.displayOnly',
    defaultMessage: 'DISPLAY ONLY has extra entities',
  },
  relido: {
    id: 'IsAtLeastAsRestrictive.relido',
    defaultMessage: 'RELIDO missing',
  },
  rdFrd: {
    id: 'IsAtLeastAsRestrictive.rdFrd',
    defaultMessage: 'RD/FRD mismatch',
  },
  cnwdi: {
    id: 'IsAtLeastAsRestrictive.cnwdi',
    defaultMessage: 'Missing CNWDI controls (RD-N/FRD-N)',
  },
  sigmaMissing: {
    id: 'IsAtLeastAsRestrictive.sigmaMissing',
    defaultMessage: 'Missing SIGMA controls',
  },
  accmMissing: {
    id: 'IsAtLeastAsRestrictive.accmMissing',
    defaultMessage: 'Missing ACCM programs',
  },
});

type MarkPropertyValidator = (a: ClassificationMark, b: ClassificationMark) => boolean;

/**
 * Returns true if the classification level of first mark given is at least as restrictive as the second
 */
const classificationLevelValidator: MarkPropertyValidator = (a, b) =>
  a.classificationLevel !== undefined &&
  b.classificationLevel !== undefined &&
  a.classificationLevel >= b.classificationLevel;

/**
 * Returns true if the first mark given has at least the FOUO/CUI/SBU/DCNI controls as the second
 *
 * Note: FOUO/CUI/SBU/DCNI only applies to unclassified information, so this will return true if the first mark is classified
 */
const hasSameUnclassifiedControls =
  (key: 'fouo' | 'cui' | 'sbu' | 'dcni'): MarkPropertyValidator =>
  (a, b) =>
    (!a[key] && !b[key]) ||
    (a.classificationLevel === LevelValueEnum.UNCLASSIFIED && !!a[key]) ||
    (a.classificationLevel !== undefined && a.classificationLevel > LevelValueEnum.UNCLASSIFIED);

/**
 * Returns true if the first mark given has the at least the same list items as the second for the given property
 */
const hasAtLeastTheSameListValidator =
  (key: ClassificationMarkStringArrayKey): MarkPropertyValidator =>
  (a, b) =>
    isSubset(b[key], a[key]);

/**
 * Returns true if the first mark given has the at least the same FGI list, or the first has NOFORN
 * If the first mark has undefined FGI and the second mark has a defined FGI (even empty), returns false
 */
const hasAtLeastTheSameFgi: MarkPropertyValidator = (a, b) => {
  const aUndefined = a.fgi === undefined;
  const bUndefined = b.fgi === undefined;
  if (aUndefined && !bUndefined) return false;

  const fgiIsSubset = isSubset(b.fgi, a.fgi);
  return fgiIsSubset || !!a.nf;
};

/**
 * Returns true if the first mark given has the same or fewer list items as the second for the given property
 */
const hasSameOrFewerListValidator =
  (key: ClassificationMarkStringArrayKey): MarkPropertyValidator =>
  (a, b) =>
    isSubset(a[key], b[key]);

/**
 * Returns true if the first mark has the same boolean property (regardless of the second mark's property), or if neither mark has the property
 */
const hasAtLeastTheSameBooleanValidator =
  (key: ConditionalKeys<ClassificationMark, boolean | undefined>): MarkPropertyValidator =>
  (a, b) =>
    !!key && (!!a[key] || !b[key]);

/**
 * Returns true if the second mark does not have RELIDO, or if the first mark has NOFORN or RELIDO
 */
const relidoValidator: MarkPropertyValidator = (a, b) => !b.relido || !!a.nf || !!a.relido;

/**
 * Returns true if the first mark has insufficient RD/FRD controls
 */
const rdFrdValidator: MarkPropertyValidator = (a, b) =>
  (!b.rd && !b.frd) || !!a.rd || (!!a.frd && !!b.frd);

const isAtLeastAsRestrictiveValidators = [
  {
    markValidator: classificationLevelValidator,
    errorMessage: messages.classificationLevel,
  },
  {
    markValidator: hasAtLeastTheSameListValidator('sci'),
    errorMessage: messages.sci,
  },
  {
    markValidator: hasAtLeastTheSameListValidator('sap'),
    errorMessage: messages.sap,
  },
  {
    markValidator: hasAtLeastTheSameFgi,
    errorMessage: messages.fgi,
  },
  {
    markValidator: hasSameUnclassifiedControls('fouo'),
    errorMessage: messages.fouo,
  },
  {
    markValidator: hasSameUnclassifiedControls('dcni'),
    errorMessage: messages.dcni,
  },
  {
    markValidator: hasSameUnclassifiedControls('cui'),
    errorMessage: messages.cui,
  },
  {
    markValidator: hasSameUnclassifiedControls('sbu'),
    errorMessage: messages.sbu,
  },
  {
    markValidator: hasAtLeastTheSameBooleanValidator('imc'),
    errorMessage: messages.imc,
  },
  {
    markValidator: hasAtLeastTheSameBooleanValidator('pr'),
    errorMessage: messages.pr,
  },
  {
    markValidator: hasAtLeastTheSameBooleanValidator('fisa'),
    errorMessage: messages.fisa,
  },
  {
    markValidator: hasAtLeastTheSameBooleanValidator('nf'),
    errorMessage: messages.nf,
  },
  {
    markValidator: hasSameOrFewerListValidator('relTo'),
    errorMessage: messages.relTo,
  },
  {
    markValidator: hasSameOrFewerListValidator('displayOnly'),
    errorMessage: messages.displayOnly,
  },
  {
    markValidator: hasAtLeastTheSameListValidator('sg'),
    errorMessage: messages.sigmaMissing,
  },
  {
    markValidator: hasAtLeastTheSameListValidator('accm'),
    errorMessage: messages.accmMissing,
  },
  { markValidator: relidoValidator, errorMessage: messages.relido },
  { markValidator: rdFrdValidator, errorMessage: messages.rdFrd },
  {
    markValidator: hasAtLeastTheSameBooleanValidator('cnwdi'),
    errorMessage: messages.cnwdi,
  },
];

/**
 * Determines if the first classification mark given is at least as restrictive as the second
 * @param firstMark the first classification mark
 * @param secondMark the second classification mark
 * @returns a boolean result value, along with an array of issues why the first mark is not as restrictive as the second
 */
function isAtLeastAsRestrictive(
  firstMark: ClassificationMark,
  secondMark: ClassificationMark
): { isValid: boolean; issues: Array<MessageDescriptor> } {
  const issues = isAtLeastAsRestrictiveValidators.reduce(
    (result: Array<MessageDescriptor>, validator) => {
      const valid = validator.markValidator(firstMark, secondMark);
      if (!valid) result.push(validator.errorMessage);
      return result;
    },
    []
  );

  const isValid = issues.length === 0;

  return { isValid, issues };
}

export default isAtLeastAsRestrictive;
export {
  classificationLevelValidator,
  relidoValidator,
  hasSameUnclassifiedControls,
  hasAtLeastTheSameListValidator,
  hasSameOrFewerListValidator,
  hasAtLeastTheSameBooleanValidator,
  isAtLeastAsRestrictiveValidators,
  hasAtLeastTheSameFgi,
  messages,
};
